<template>
  <div>
    <div class="font-bold p-2">Shape</div>
    <div class="flex gap-2 p-2 text-gray-300">
      <SMButton v-tippy="'Duplicate'" icon="copy" :on-click="duplicate" />
      <SMButton v-tippy="'Delete'" icon="delete" :on-click="remove" />
    </div>
    <div class="md-subheading px-2 my-2 bg-gray-200">Styles</div>
    <ul class="space-y-2 p-2">
      <li v-if="!isLine">
        <SMColorPicker v-model="fillColor" v-tippy="'Fill Color'" icon="fill" class="grow" />
      </li>
      <li>
        <SMNumberInput
          v-model="stroke.width.value"
          v-tippy="'Stroke width'"
          :min="0"
          :max="10"
          icon="stroke-width"
          @change="onStrokeWidthChange"
        />
      </li>
      <li>
        <SMColorPicker v-model="strokeColor" v-tippy="'Stroke Color'" icon="stroke-color" class="grow" />
      </li>
      <li v-if="isRect">
        <SMNumberInput
          v-model="cornerRadius.value"
          v-tippy="'Smoothing'"
          :min="cornerRadius.min"
          :max="cornerRadius.max"
          icon="corner-radius"
          @change="onCornerRadiusChange"
        />
      </li>
    </ul>
  </div>
</template>
<script lang="ts">
import tinycolor from 'tinycolor2';
import eventbus from 'tiny-emitter/instance';
import first from 'lodash/first';
import SMButton from '@/components/ui/SMButton.vue';
import SMNumberInput from '@/components/ui/SMNumberInput.vue';
import SMColorPicker from '@/components/ui/SMColorPicker.vue';
import { TYPE } from '@/constants';

const whiteRgba = tinycolor('white').toRgb();
const blackRgba = tinycolor('black').toRgb();

export default {
  components: {
    SMButton,
    SMNumberInput,
    SMColorPicker,
  },
  props: ['units'],
  data() {
    return {
      stroke: {
        width: {
          tooltip: false,
          width: 200,
          value: 1,
          range: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
          interval: 1,
        },
        color: {
          rgba: blackRgba,
        },
      },
      fill: {
        rgba: whiteRgba,
      },
      cornerRadius: {
        tooltip: false,
        width: 200,
        value: 0,
        min: 0,
        max: 50,
        interval: 5,
      },
    };
  },
  computed: {
    strokeColor: {
      get() {
        return tinycolor(this.stroke.color.rgba).toRgbString();
      },
      set(value) {
        this.updateStrokeColor({ rgba: value });
      },
    },
    fillColor: {
      get() {
        return tinycolor(this.fill.rgba).toRgbString();
      },
      set(value) {
        this.updateFillColor({ rgba: value });
      },
    },
    firstUnit() {
      return first(this.units);
    },
    isRect() {
      return this.firstUnit.subtype === TYPE.RECT;
    },
    isLine() {
      return this.firstUnit.subtype === TYPE.LINE;
    },
  },
  created() {
    const { 'stroke-width': strokeWidth, fill, stroke } = this.firstUnit.attributes.styles;
    this.stroke.width.value = strokeWidth ? parseInt(strokeWidth, 10) : 1;
    if (this.isRect) {
      this.cornerRadius.value = this.firstUnit.radius;
    }
    this.fill.rgba = fill ? tinycolor(fill).toRgb() : whiteRgba;
    this.stroke.color.rgba = stroke ? tinycolor(stroke).toRgb() : blackRgba;
  },
  methods: {
    updateFillColor({ rgba }) {
      this.setStyle('fill', tinycolor(rgba).toRgbString());
      this.setStyle('fill-opacity', 1);
      this.fill.rgba = rgba;
      eventbus.emit('selection:delayedHistory', `${this.firstUnit.typeTextual} fill color change`);
    },
    updateStrokeColor({ rgba }) {
      this.setStyle('stroke', tinycolor(rgba).toRgbString());
      this.stroke.color.rgba = rgba;
      eventbus.emit('selection:delayedHistory', `${this.firstUnit.typeTextual} stroke color change`);
    },
    onStrokeWidthChange(val) {
      this.setStyle('stroke-width', val);
      this.stroke.width.value = val;
      eventbus.emit('selection:delayedHistory', `${this.firstUnit.typeTextual} stroke width change`);
    },
    onCornerRadiusChange(val) {
      this.firstUnit.radius = val;
    },
    setAttribute(name, value) {
      this.firstUnit.setAttribute(name, value);
    },
    setStyle(name, value) {
      this.firstUnit.setStyle(name, value);
    },
    duplicate() {
      eventbus.emit('selection:clone');
    },
    remove() {
      eventbus.emit('selection:remove');
    },
  },
};
</script>
